import React, { useState, useEffect } from "react";
import TransactionService from "../../../services/api/transaction.service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Withdrawal = ({ walletData, onWalletUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [withdrawalMethod, setWithdrawalMethod] = useState("UPI");
  const [upiId, setUpiId] = useState("");
  const [bankAccountDetails, setBankAccountDetails] = useState({
    accountNumber: "",
    bankName: "",
    ifscCode: "",
    accountHolderName: "",
  });
  const [contactId, setContactId] = useState(null);
  const [fundAccountId, setFundAccountId] = useState(null);
  const [withdrawalDetails, setWithdrawalDetails] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    fetchWithdrawalRequests();
  }, [pagination.page]);

  useEffect(() => {

  }, [pendingRequest]);

  const [withdrawalAmountError, setWithdrawalAmountError] = useState(null);

  useEffect(() => {
    const amountValue = parseFloat(withdrawalAmount);
    if (walletData?.balance < amountValue) {
      setWithdrawalAmountError("Insufficient balance for this withdrawal.");
    } else {
      setWithdrawalAmountError(null);
    }
  }, [withdrawalAmount, walletData?.balance]);

  const fetchWithdrawalRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const response = await TransactionService.getUserWithdrawalRequests(
        pagination.page,
        pagination.limit
      );


      if (
        response?.success &&
        response?.data?.success &&
        Array.isArray(response?.data?.data?.requests)
      ) {
        const requests = response.data.data.requests;
        const paginationData = response.data.data.pagination;


        setWithdrawalRequests(requests);

        const pending = requests.find((req) => req.status === "PENDING");


        setPendingRequest(pending || null);

        setPagination({
          page: paginationData?.currentPage || 1,
          limit: pagination.limit,
          total: paginationData?.totalRequests || 0,
          totalPages: paginationData?.totalPages || 1,
          hasNext: paginationData?.hasNext || false,
          hasPrev: paginationData?.hasPrev || false,
        });
      } else {
        toast.error(response?.error || "Invalid response format");
      }
    } catch (error) {
      toast.error("An error occurred while fetching withdrawal requests");
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleBankInputChange = (e) => {
    const { name, value } = e.target;
    setBankAccountDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleWithdrawal = async (e) => {
    if (e) e.preventDefault();
    if (isProcessing) return;

    if (pendingRequest) {
      setError(
        "You already have a pending withdrawal request. Please wait for it to be processed."
      );
      toast.error(
        "You already have a pending withdrawal request. Please wait for it to be processed."
      );
      return;
    }

    const amountValue = parseFloat(withdrawalAmount);

    if (isNaN(amountValue) || amountValue <= 0) {
      setError("Please enter a valid positive amount.");
      toast.error("Please enter a valid positive amount.");
      return;
    }
    if (walletData?.balance < amountValue) {
      setError("Insufficient balance for this withdrawal.");
      toast.error("Insufficient balance for this withdrawal.");
      return;
    }
    if (walletData?.user?.isKycVerified === false) {
      setError("KYC verification is required for withdrawals.");
      toast.error("KYC verification is required for withdrawals.");
      return;
    }

    let payload = {
      amount: amountValue,
      withdrawalMethod: withdrawalMethod,
    };

    if (withdrawalMethod === "UPI") {
      if (!upiId) {
        setError("UPI ID is required for UPI withdrawal.");
        toast.error("UPI ID is required for UPI withdrawal.");
        return;
      }
      payload.upiId = upiId;
    } else if (withdrawalMethod === "BANK") {
      const { accountNumber, bankName, ifscCode, accountHolderName } =
        bankAccountDetails;
      if (!accountNumber || !bankName || !ifscCode || !accountHolderName) {
        setError("All bank account details are required for bank withdrawal.");
        toast.error(
          "All bank account details are required for bank withdrawal."
        );
        return;
      }
      payload.bankAccount = bankAccountDetails;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage("");

    try {
      const withdrawalResponse = await TransactionService.initiateWithdrawal(
        payload
      );

      if (withdrawalResponse?.success) {
        setWithdrawalDetails(withdrawalResponse.data);
        setContactId(withdrawalResponse.data.contactId || null);
        setFundAccountId(withdrawalResponse.data.fundAccountId || null);

        setSuccessMessage("Withdrawal request initiated successfully!");
        toast.success("Withdrawal request initiated successfully!");

        setWithdrawalAmount("");
        setUpiId("");
        setBankAccountDetails({
          accountNumber: "",
          bankName: "",
          ifscCode: "",
          accountHolderName: "",
        });

        if (onWalletUpdate) {
          onWalletUpdate();
        }

        fetchWithdrawalRequests();

        setTimeout(() => {
          setSuccessMessage("");
        }, 5000);
      } else {
        setError(withdrawalResponse?.error || "Failed to initiate withdrawal.");
        toast.error(
          withdrawalResponse?.error || "Failed to initiate withdrawal."
        );
      }
    } catch (err) {
      setError(
        `An unexpected error occurred: ${err.message || "Unknown error"}`
      );
      toast.error(
        `An unexpected error occurred: ${err.message || "Unknown error"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelWithdrawal = async () => {
    if (!pendingRequest || !pendingRequest._id) {
      toast.error("No pending request found to cancel.");
      return;
    }

    setLoading(true);
    try {
      const response = await TransactionService.cancelWithdrawalRequest(
        pendingRequest._id
      );

      if (response.success) {
        toast.success("Withdrawal request cancelled successfully.");
        // Refresh the withdrawal requests to update the UI
        fetchWithdrawalRequests();
        setPendingRequest(null); // Clear pending request state
      } else {
        toast.error(response.error || "Failed to cancel withdrawal request.");
      }
    } catch (error) {
      toast.error("An error occurred while cancelling the withdrawal request.");
    } finally {
      setLoading(false);
    }
  };

  const toggleHistory = () => {
    setShowHistory(!showHistory);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusDetails = (status) => {
    switch (status) {
      case "PENDING":
        return { color: "bg-yellow-100 text-yellow-800", text: "Pending" };
      case "APPROVED":
        return { color: "bg-green-100 text-green-800", text: "Approved" };
      case "REJECTED":
        return { color: "bg-red-100 text-red-800", text: "Rejected" };
      case "COMPLETED":
        return { color: "bg-blue-100 text-blue-800", text: "Completed" };
      default:
        return { color: "bg-gray-100 text-gray-800", text: status };
    }
  };

  const getMethodDetails = (method, request) => {
    if (method === "UPI") {
      return `UPI (${request.upiId || "N/A"})`;
    } else if (method === "BANK") {
      return `Bank (${request.bankAccount?.accountNumber
        ? "••••" + request.bankAccount.accountNumber.slice(-4)
        : "N/A"
        })`;
    }
    return method;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 sm:pt-6 lg:px-8 bg-white rounded-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Withdraw Funds</h2>

      {error && (
        <div className="bg-red-50 border border-red-400 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-400 text-green-700 p-4 rounded-lg mb-6">
          {successMessage}
        </div>
      )}

      {/* Pending Request Section */}
      {pendingRequest ? (
        <div className="mb-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h3 className="text-lg font-semibold mb-3 text-yellow-800">
            Pending Withdrawal Request
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="font-medium text-gray-600">Requested At:</div>
            <div>{formatDate(pendingRequest.requestedAt)}</div>
            <div className="font-medium text-gray-600">Amount:</div>
            <div>
              {pendingRequest.amount.toFixed(2)}{" "}
              {pendingRequest.currency || "INR"}
            </div>
            <div className="font-medium text-gray-600">Method:</div>
            <div>
              {getMethodDetails(
                pendingRequest.withdrawalMethod,
                pendingRequest
              )}
            </div>
            <div className="font-medium text-gray-600">Status:</div>
            <div>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800`}
              >
                Pending
              </span>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleCancelWithdrawal}
              disabled={loading}
              className={`px-4 py-2 rounded-md text-sm font-medium ${loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700 text-white focus:ring focus:ring-red-200 focus:ring-opacity-50"
                }`}
            >
              {loading ? "Cancelling..." : "Cancel Request"}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-6 text-gray-500 text-sm">
          No pending request found
        </div>
      )}

      {/* Withdrawal Form Section */}
      {!pendingRequest && (
        <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold mb-3 text-blue-800">
            Request a Withdrawal
          </h3>
          <p className="text-blue-700 mb-4 text-sm">
            Choose your preferred method and enter the details.
          </p>

          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Withdrawal Method
              </label>
              <select
                value={withdrawalMethod}
                onChange={(e) => setWithdrawalMethod(e.target.value)}
                className="mt-1 block w-full max-w-xs h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
              >
                <option value="UPI">UPI</option>
                <option value="BANK">Bank Transfer</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Amount (INR)
              </label>
              <input
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                className="mt-1 block w-full max-w-xs h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                placeholder="Enter amount"
                min="0"
                step="0.01"
              />
              {withdrawalAmountError && (
                <p className="text-red-500 text-xs mt-1">
                  {withdrawalAmountError}
                </p>
              )}
            </div>

            {withdrawalMethod === "UPI" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  UPI ID
                </label>
                <input
                  type="text"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  className="mt-1 block w-full max-w-xs h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                  placeholder="Enter UPI ID"
                />
              </div>
            )}

            {withdrawalMethod === "BANK" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Account Number
                  </label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={bankAccountDetails.accountNumber}
                    onChange={handleBankInputChange}
                    className="mt-1 block w-full max-w-xs h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                    placeholder="Enter account number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={bankAccountDetails.bankName}
                    onChange={handleBankInputChange}
                    className="mt-1 block w-full max-w-xs h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                    placeholder="Enter bank name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    name="ifscCode"
                    value={bankAccountDetails.ifscCode}
                    onChange={handleBankInputChange}
                    className="mt-1 block w-full max-w-xs h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                    placeholder="Enter IFSC code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Account Holder Name
                  </label>
                  <input
                    type="text"
                    name="accountHolderName"
                    value={bankAccountDetails.accountHolderName}
                    onChange={handleBankInputChange}
                    className="mt-1 block w-full max-w-xs h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                    placeholder="Enter account holder name"
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleWithdrawal}
              disabled={isProcessing}
              className={`w-full max-w-xs h-10 rounded-md text-white font-medium text-sm ${isProcessing
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                }`}
            >
              {isProcessing ? "Processing..." : "Request Withdrawal"}
            </button>
          </div>
        </div>
      )}

      {/* Transaction Details Display */}
      {/* {withdrawalDetails && (
        <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">
            Transaction Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="font-medium text-gray-600">Contact ID:</div>
            <div>{contactId || "N/A"}</div>
            <div className="font-medium text-gray-600">Fund Account ID:</div>
            <div>{fundAccountId || "N/A"}</div>
            <div className="font-medium text-gray-600">Amount:</div>
            <div>
              {withdrawalDetails.amount?.toFixed(2)}{" "}
              {withdrawalDetails.currency || "INR"}
            </div>
            <div className="font-medium text-gray-600">Method:</div>
            <div>
              {getMethodDetails(
                withdrawalDetails.withdrawalMethod,
                withdrawalDetails
              )}
            </div>
          </div>
        </div>
      )} */}

      {/* Withdrawal History Section */}
      <div className="mt-8">
        <button
          onClick={toggleHistory}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium focus:ring focus:ring-gray-200 focus:ring-opacity-50"
        >
          {showHistory ? "Hide Withdrawal History" : "Show Withdrawal History"}
        </button>

        {showHistory && (
          <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">
              Withdrawal History
            </h3>

            {isLoadingRequests ? (
              <div className="flex justify-center items-center py-4">
                <svg
                  className="animate-spin h-5 w-5 text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="ml-2 text-sm text-gray-600">
                  Loading requests...
                </span>
              </div>
            ) : withdrawalRequests.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No withdrawal requests found.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                          Date
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                          Amount
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                          Method
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                          Status
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {withdrawalRequests.map((request) => {
                        const status = getStatusDetails(request.status);
                        return (
                          <tr key={request._id}>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 w-1/6">
                              {formatDate(request.requestedAt)}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 w-1/6">
                              {request.amount.toFixed(2)}{" "}
                              {request.currency || "INR"}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 w-1/5">
                              {getMethodDetails(
                                request.withdrawalMethod,
                                request
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm w-1/6">
                              <span
                                className={`px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
                              >
                                {status.text}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600 w-1/3">
                              {request.status === "REJECTED" &&
                                request.rejectionReason && (
                                  <div className="text-red-600 text-sm">
                                    Reason: {request.rejectionReason}
                                  </div>
                                )}
                              {request.status === "APPROVED" &&
                                request.adminNotes && (
                                  <div className="text-green-600 text-sm">
                                    Notes: {request.adminNotes}
                                  </div>
                                )}
                              {request.transactionId && (
                                <div className="text-gray-600 text-sm">
                                  Transaction ID: {request.transactionId}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 gap-4">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPrev}
                      className="px-3 py-1.5 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNext}
                      className="px-3 py-1.5 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Withdrawal;
